// src/components/AddressInput.js
import React, { useState, useEffect, useRef } from "react";
import useLocalStorage from "../hooks/useLocalStorage";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { components as RSComponents } from "react-select";

// Lista de tipos de vía
const STREET_TYPES = ["Calle", "Carrera", "Diagonal", "Transversal"];

// Lista de barrios disponibles
const BARRIOS = [
  "Atenas",
  "villa maría",
  "Berlín",
  "Bilbao",
  "Cañiza I",
  "Cañiza II",
  "Cañiza III",
  "Carolina II",
  "Carolina III",
  "Compartir",
  "El Cedro",
  "Fontanar del Río",
  "La Gaitana",
  "La Isabela",
  "Lisboa",
  "Los Nogales de Tibabuyes",
  "Miramar",
  "Nueva Tibabuyes",
  "Nuevo Corinto",
  "Prados de Santa Bárbara",
  "Rincón de Boyacá",
  "Sabana de Tibabuyes",
  "San Carlos de Suba",
  "San Carlos de Tibabuyes",
  "San Pedro de Tibabuyes",
  "Santa Cecilia",
  "Santa Rita",
  "Tibabuyes Universal",
  "Toscana",
  "Vereda Suba-Rincón",
  "Vereda Tibabuyes",
  "Verona",
  "Villa Cindy",
  "Villa de las Flores",
  "Villa Gloria",
];

// Convertir arrays a formato react-select
const streetTypeOptions = STREET_TYPES.map((s) => ({ value: s, label: s }));
const barrioOptions = BARRIOS.map((b) => ({ value: b, label: b }));

// Estilos customizados para react-select
const customStyles = {
  control: (base, state) => ({
    ...base,
    borderRadius: "0.375rem",
    borderColor: state.isFocused ? "#22c55e" : "#d1d5db",
    boxShadow: state.isFocused ? "0 0 0 2px #22c55e33" : "none",
    "&:hover": { borderColor: "#22c55e" },
    padding: "2px",
    minHeight: "42px",
    WebkitTapHighlightColor: 'transparent',
    touchAction: 'manipulation',
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "#22c55e"
      : state.isFocused
      ? "#bbf7d0"
      : "white",
    color: state.isSelected ? "white" : "#374151",
    "&:active": {
      backgroundColor: "#22c55e",
      color: "white",
    },
    fontSize: "0.875rem",
    padding: "8px 12px",
    borderBottom: "1px solid #e5e7eb",
    WebkitTapHighlightColor: 'transparent',
    touchAction: 'manipulation',
  }),
  placeholder: (base) => ({
    ...base,
    color: "#6b7280",
    fontSize: "0.875rem",
    fontWeight: "600",
  }),
  singleValue: (base) => ({
    ...base,
    fontSize: "0.9rem",
    color: "#374151",
  }),
  menu: (base) => ({
    ...base,
    borderRadius: "0.5rem",
    zIndex: 50,
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 99999,
  }),
};

// Componente de opción táctil para iOS: fuerza selección en un solo tap
const TouchOption = (props) => {
  const { data, selectOption } = props;
  const nextInnerProps = {
    ...props.innerProps,
    onTouchStart: (e) => {
      e.stopPropagation();
      if (props.innerProps?.onTouchStart) props.innerProps.onTouchStart(e);
    },
    onTouchEnd: (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!props.isDisabled) selectOption(data);
      if (props.innerProps?.onTouchEnd) props.innerProps.onTouchEnd(e);
    },
    onMouseDown: (e) => {
      // fallback para navegadores que traduzcan touch a click
      e.preventDefault();
      e.stopPropagation();
      if (!props.isDisabled) selectOption(data);
      if (props.innerProps?.onMouseDown) props.innerProps.onMouseDown(e);
    }
  };
  return (
    <RSComponents.Option
      {...props}
      innerProps={nextInnerProps}
    />
  );
};

// 🔥 Normalizador de teléfonos
const normalizePhone = (value) => {
  let clean = (value || "").replace(/\s+/g, "");
  if (clean.startsWith("+57")) clean = clean.slice(3);
  if (clean.startsWith("57") && clean.length > 10) clean = clean.slice(2);
  if (clean.startsWith("0") && clean.length === 11) clean = clean.slice(1);
  return clean;
};

const InputField = ({
  id,
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  error,
  autoComplete,
  placeholderBold = false,
}) => (
  <div className="mb-3">
    <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
      {label}
    </label>
    <input
      id={id}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      autoComplete={autoComplete}
      inputMode={type === 'tel' ? 'numeric' : undefined}
      onTouchStart={(e) => {
        // Evitar que contenedores padres consuman el toque
        e.stopPropagation();
      }}
      onTouchMove={(e) => {
        // Evitar que el movimiento táctil dentro del input propague un swipe al padre
        e.stopPropagation();
      }}
      onTouchEnd={(e) => {
        // iOS: asegurar foco con un solo tap y evitar click fantasma
        // No forzar foco si acabamos de cambiar de slide
        try { if (window.__lastSlideChange && (Date.now() - window.__lastSlideChange) < 350) return; } catch(_){}
        e.preventDefault();
        e.stopPropagation();
        const el = e.currentTarget;
        // Foco sin scroll y cursor al final
        if (el && typeof el.focus === 'function') {
          try { el.focus({ preventScroll: true }); } catch (_) { el.focus(); }
          const len = (el.value || '').length;
          try { el.setSelectionRange(len, len); } catch (_) {}
        }
      }}
      style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent', ...(placeholderBold ? { fontWeight: value ? 'normal' : '600' } : {}) }}
      className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 ${
        error ? "border-red-500 focus:ring-red-500" : "focus:ring-green-500"
      } ${placeholderBold ? 'placeholder-bold' : ''}`}
      style={placeholderBold ? { fontWeight: value ? 'normal' : '600' } : {}}
    />
    {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
  </div>
);

const AddressInput = ({ onConfirm, onValidityChange, initialAddress }) => {
  const [formData, setFormData] = useLocalStorage("addressForm", {
    streetType: "Calle",
    streetNumber: "",
    houseNumber: "",
    neighborhood: "",
    details: "",
    phoneNumber: "",
  });
  const [errors, setErrors] = useState({});
  const lastTouchTimeRef = useRef(0);
  const touchInfoRef = useRef({ x: 0, y: 0, moved: false });
  const [isConfirmed, setIsConfirmed] = useState(false);
  const streetTypeRef = useRef(null);
  const barrioRef = useRef(null);
  const [streetMenuOpen, setStreetMenuOpen] = useState(false);
  const [barrioMenuOpen, setBarrioMenuOpen] = useState(false);
  const rootRef = useRef(null);
  const onValidityChangeRef = useRef(onValidityChange);

  onValidityChangeRef.current = onValidityChange;

  const isValidPhone = (phone) => /^3\d{9}$/.test(phone);

  // Validación
  useEffect(() => {
    const newErrors = {};
    if (!formData.streetNumber) newErrors.streetNumber = "Campo requerido.";
    if (!formData.houseNumber) newErrors.houseNumber = "Campo requerido.";
    if (!formData.neighborhood) newErrors.neighborhood = "Selecciona un barrio.";
    if (!formData.phoneNumber) {
      newErrors.phoneNumber = "Campo requerido.";
    } else if (!isValidPhone(formData.phoneNumber)) {
      newErrors.phoneNumber = "Formato no válido (Ej: 3001234567).";
    }
    
    // Validar rango permitido del número de vía (solo 100 a 155)
    if (formData.streetNumber) {
      const streetNum = parseInt(formData.streetNumber.replace(/\D/g, ''), 10);
      if (!isNaN(streetNum) && (streetNum < 100 || streetNum > 155)) {
        newErrors.streetNumber = "⚠️ Dirección supera los 4km del restaurante";
      }
    }
    
    setErrors((prev) => {
      const prevKey = JSON.stringify(prev);
      const nextKey = JSON.stringify(newErrors);
      return prevKey === nextKey ? prev : newErrors;
    });
  }, [formData]);

  // 🔥 Si Google mete "Casa 39" en Número, lo movemos a Instrucciones
  useEffect(() => {
    if (/^(Casa|Apto|Apartamento|Torre)/i.test(formData.houseNumber)) {
      setFormData((prev) => ({
        ...prev,
        details: prev.details || prev.houseNumber,
        houseNumber: "",
      }));
    }
  }, [formData.houseNumber]);

  const handleInputChange = (e) => {
    const { id, value } = e.target;

    if (id === "phoneNumber") {
      setFormData((prev) => ({ ...prev, [id]: normalizePhone(value) }));
      return;
    }

    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const handleStreetTypeChange = (selected) => {
    setFormData((prev) => ({ ...prev, streetType: selected?.value || "" }));
  };

  const handleNeighborhoodChange = (selected, actionMeta) => {
    // Si es un valor creado por el usuario (escribió algo que no está en la lista)
    if (actionMeta?.action === 'create-option') {
      setFormData((prev) => ({ ...prev, neighborhood: selected?.value || "" }));
    } else {
      setFormData((prev) => ({ ...prev, neighborhood: selected?.value || "" }));
    }
  };

  const isFormValid = Object.keys(errors).length === 0;

  // Notificar validez al padre (para ocultar/mostrar mensajes contextuales)
  useEffect(() => {
    onValidityChangeRef.current?.(isFormValid);
  }, [isFormValid]);

  const handleConfirm = () => {
    if (!isFormValid) return;
    setIsConfirmed(true);

    const addressFormatted = `${formData.streetType} ${formData.streetNumber} # ${formData.houseNumber}`;
    const confirmedDetails = {
      address: addressFormatted,
      neighborhood: formData.neighborhood,
      details: formData.details,
      phoneNumber: normalizePhone(formData.phoneNumber),
    };

    onConfirm?.(confirmedDetails);
  };

  // Confirmar con un toque en iOS/Android evitando doble disparo
  const onTouchStartGeneric = (e) => {
    const t = e.touches && e.touches[0];
    if (!t) return;
    // Evitar que el contenedor padre capture el inicio del gesto
    try { e.stopPropagation(); } catch (_) {}
    touchInfoRef.current = { x: t.clientX, y: t.clientY, moved: false };
  };

  const onTouchMoveGeneric = (e) => {
    const t = e.touches && e.touches[0];
    if (!t) return;
    // Evitar que el movimiento propague y active el stepper padre
    try { e.stopPropagation(); } catch (_) {}
    const dx = t.clientX - touchInfoRef.current.x;
    const dy = t.clientY - touchInfoRef.current.y;
    if (Math.abs(dx) > 8 || Math.abs(dy) > 8) touchInfoRef.current.moved = true;
  };

  const shouldIgnoreTap = () => {
    const draggedRecently = (typeof window !== 'undefined' && window.__lastMealDragTime)
      ? (Date.now() - window.__lastMealDragTime) < 300
      : false;
    const draggingNow = (typeof window !== 'undefined' && window.__isMealDragging) ? true : false;
    return draggingNow || draggedRecently || touchInfoRef.current.moved;
  };

  const handleConfirmTap = (e) => {
    if (!isFormValid) return;
    if (shouldIgnoreTap()) return;
    if (e?.type === 'touchend') {
      e.preventDefault();
      e.stopPropagation();
      lastTouchTimeRef.current = Date.now();
      handleConfirm();
      return;
    }
    if (Date.now() - lastTouchTimeRef.current < 350) return;
    handleConfirm();
  };

  // iOS: abrir Select/CreatableSelect con un solo toque (sin doble tap)
  const onSelectTouchStart = (e) => {
    const t = e.touches && e.touches[0];
    if (!t) return;
    touchInfoRef.current = { x: t.clientX, y: t.clientY, moved: false };
    e.stopPropagation();
  };
  const onSelectTouchMove = (e) => {
    const t = e.touches && e.touches[0];
    if (!t) return;
    // Evitar propagar swipe al padre mientras se interactúa con el select
    try { e.stopPropagation(); } catch (_) {}
    const dx = Math.abs(t.clientX - touchInfoRef.current.x);
    const dy = Math.abs(t.clientY - touchInfoRef.current.y);
    if (dx > 10 || dy > 10) touchInfoRef.current.moved = true;
  };
  const focusAndOpenMenu = (ref) => {
    if (!ref?.current) return;
    try { ref.current.focus(); } catch (_) {}
  };
  const onSelectTouchEnd = (ref, isOpenSetter, isOpen) => (e) => {
    if (touchInfoRef.current.moved) return;
    // Primer tap: abrir menú; si ya está abierto, dejar que opción reciba el evento
    if (!isOpen) {
      e.preventDefault();
      e.stopPropagation();
      lastTouchTimeRef.current = Date.now();
      focusAndOpenMenu(ref);
      // react-select abre menú con focus; aseguramos flag abierto
      isOpenSetter(true);
    }
  };

  // siempre mostrar teléfono limpio
  const phoneValue = normalizePhone(formData.phoneNumber);

  // Escuchar cambios de slide para resetear estados interactivos (menus, focus)
  useEffect(() => {
    const handler = (e) => {
      try { e.stopPropagation(); } catch (_) {}
      // Cerrar menús y desenfocar inputs/textarea dentro del componente
      setStreetMenuOpen(false);
      setBarrioMenuOpen(false);
      touchInfoRef.current.moved = false;
      try {
        if (rootRef.current) {
          const els = rootRef.current.querySelectorAll('input, textarea, [role="combobox"]');
          els.forEach((el) => {
            try { if (typeof el.blur === 'function') el.blur(); } catch (_) {}
          });
        }
      } catch (_) {}
      // No confirmamos automáticamente; simplemente cerramos/limpiamos
      setIsConfirmed(false);
    };
    window.addEventListener('slideChanged', handler, { capture: true });
    return () => window.removeEventListener('slideChanged', handler, { capture: true });
  }, []);

  if (isConfirmed) {
    return (
      <div ref={rootRef} className="bg-white p-4 rounded-lg shadow space-y-3 text-sm sm:text-base">
        <h4 className="font-semibold text-gray-800">📋 Dirección guardada</h4>
        <p>
          <span className="font-medium text-blue-600">Dirección</span>
          <br />
          {formData.streetType} {formData.streetNumber} # {formData.houseNumber}
        </p>
        <p>
          <span className="font-medium text-blue-600">Barrio</span>
          <br />
          {formData.neighborhood}
        </p>
        {formData.details && (
          <p>
            <span className="font-medium text-blue-600">Instrucciones de entrega</span>
            <br />
            {formData.details}
          </p>
        )}
        <p>
          <span className="font-medium text-blue-600">Teléfono</span>
          <br />
          {formData.phoneNumber}
        </p>
        <button
          onClick={(e) => { e.stopPropagation(); setIsConfirmed(false); }}
          onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); setIsConfirmed(false); }}
          className="mt-3 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md"
        >
          Editar dirección
        </button>
      </div>
    );
  }

  return (
    <div ref={rootRef} className="bg-white p-4 rounded-lg shadow space-y-4 text-sm sm:text-base">
      {/* Tipo de vía */}
      <div>
        <label className="block font-medium text-gray-700 mb-1">Tipo de vía</label>
        <Select
          ref={streetTypeRef}
          options={streetTypeOptions}
          value={streetTypeOptions.find((o) => o.value === formData.streetType) || null}
          onChange={handleStreetTypeChange}
          placeholder="Selecciona tipo de vía"
          styles={customStyles}
          classNamePrefix="react-select"
          openMenuOnFocus
          isSearchable={false}
          closeMenuOnSelect
          onTouchStart={onSelectTouchStart}
          onTouchMove={onSelectTouchMove}
          onTouchEnd={onSelectTouchEnd(streetTypeRef, setStreetMenuOpen, streetMenuOpen)}
          onMenuOpen={() => setStreetMenuOpen(true)}
          onMenuClose={() => setStreetMenuOpen(false)}
          components={{ Option: TouchOption }}
          menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
        />
      </div>

      {/* Números */}
      <div className="grid grid-cols-2 gap-4">
        <InputField
          id="streetNumber"
          label="Número de vía"
          value={formData.streetNumber}
          onChange={handleInputChange}
          placeholder="Ej: 137ABis"
          error={errors.streetNumber}
          autoComplete="address-line1"
          placeholderBold={true}
        />
        <InputField
          id="houseNumber"
          label="Número"
          value={formData.houseNumber}
          onChange={handleInputChange}
          placeholder="Ej: 128b-01"
          error={errors.houseNumber}
          autoComplete="address-line2"
          placeholderBold={true}
        />
      </div>

      {/* Barrio */}
      <div>
        <label className="block font-medium text-gray-700 mb-1">Barrio (pon cualquiera si no sabes cuál es)</label>
        <CreatableSelect
          ref={barrioRef}
          options={barrioOptions}
          value={
            formData.neighborhood 
              ? barrioOptions.find((o) => o.value === formData.neighborhood) || { value: formData.neighborhood, label: formData.neighborhood }
              : null
          }
          onChange={handleNeighborhoodChange}
          placeholder="Escribe o selecciona un barrio"
          styles={customStyles}
          classNamePrefix="react-select"
          isSearchable
          isClearable
          formatCreateLabel={(inputValue) => `Usar: "${inputValue}"`}
          openMenuOnFocus
          closeMenuOnSelect
          onTouchStart={onSelectTouchStart}
          onTouchMove={onSelectTouchMove}
          onTouchEnd={onSelectTouchEnd(barrioRef, setBarrioMenuOpen, barrioMenuOpen)}
          onMenuOpen={() => setBarrioMenuOpen(true)}
          onMenuClose={() => setBarrioMenuOpen(false)}
          components={{ Option: TouchOption }}
          menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
        />
        {errors.neighborhood && (
          <p className="text-red-500 text-xs mt-1">{errors.neighborhood}</p>
        )}
      </div>

      {/* Instrucciones */}
      <InputField
        id="details"
        label="Instrucciones de entrega (opcional)"
        value={formData.details}
        onChange={handleInputChange}
        placeholder="Ej: Nombre, Colegio, Apto 302, int 3, Cade..."
        autoComplete="address-line3"
        placeholderBold={true}
      />

      {/* Teléfono */}
      <InputField
        id="phoneNumber"
        label="Número de teléfono"
        value={phoneValue}
        onChange={handleInputChange}
        placeholder="Ej: 3001234567"
        error={errors.phoneNumber}
        type="tel"
        autoComplete="tel"
        placeholderBold={true}
      />

      <button
        onClick={handleConfirmTap}
        onTouchStart={onTouchStartGeneric}
        onTouchMove={onTouchMoveGeneric}
        onTouchEnd={handleConfirmTap}
        disabled={!isFormValid}
        className={`w-full mt-2 bg-green-500 hover:bg-green-600 text-white font-semibold px-4 py-2 rounded-md transition-colors ${
          !isFormValid ? "opacity-50 cursor-not-allowed" : ""
        }`}
      >
        Confirmar dirección
      </button>
    </div>
  );
};

export default AddressInput;
